import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Container } from '../layout/AppLayout.js';
import Spinner from '../shared/Spinner.js';
import Header from "../shared/Header.js";
import { useNotifications } from '../shared/Notifications.js';
import EvidenceModal from '../shared/EvidenceModal.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faImage } from '@fortawesome/free-solid-svg-icons';

const formatAlertDate = (date) => {
  const d = new Date(Date.parse(date))
  const region = "es-CL"
  return `${d.toLocaleDateString(region)} ${d.toLocaleTimeString(region)}`
}

function EvidencesList({ evidences }) {
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageErrors, setImageErrors] = useState({});
  const itemsPerPage = 6;

  console.log('Evidences data:', evidences); // Debug

  if (!evidences || evidences.length === 0) {
    return (
      <div className="text-center text-muted py-4 my-4">
        Sin evidencias asociadas
      </div>
    );
  }

  const totalPages = Math.ceil(evidences.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEvidences = evidences.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div>
      <div className="row">
        {currentEvidences.map((item, index) => (
          <div key={item.id} className="col-6 mb-3">
            <div
              className="border rounded p-2 text-center"
              style={{
                cursor: 'pointer',
                height: '120px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setSelectedEvidence(item)}
            >
              {item.mime_type && item.mime_type.startsWith('video/') ? (
                <div className="text-center">
                  <FontAwesomeIcon icon={faImages} size="3x" className="text-muted" />
                  <div className="mt-2 small text-muted">Video</div>
                </div>
              ) : imageErrors[item.id] ? (
                <div className="text-center">
                  <FontAwesomeIcon icon={faImage} size="3x" className="text-muted" />
                  <div className="mt-2 small text-muted">Imagen</div>
                  <div className="mt-1 small text-danger">Error al cargar evidencia</div>
                </div>
              ) : (
                <img
                  src={item.url}
                  alt={`Evidencia ${startIndex + index + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    console.error('Error loading image:', item.url);
                    setImageErrors(prev => ({ ...prev, [item.id]: true }));
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            ‹ Anterior
          </button>
          <span className="text-muted small">
            Página {currentPage} de {totalPages} ({evidences.length} evidencias)
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Siguiente ›
          </button>
        </div>
      )}

      {selectedEvidence && (
        <EvidenceModal
          files={[selectedEvidence]}
          onClose={() => setSelectedEvidence(null)}
        />
      )}
    </div>
  );
}

export default function ReportDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const notifications = useNotifications();

  const { isLoading, data } = useQuery(`devicereport/detail/${params.ReportId}`);
  const report = data && data.result;

  useEffect(() => {
    if (!isLoading && !report) {
      notifications.warning('El reporte solicitado no existe');
      navigate(`/sensors`);
    }
  }, [report, isLoading, navigate, notifications]);

  if (isLoading) {
    return (
      <Container>
        <div className="text-center my-4 py-4">
          <Spinner />
        </div>
      </Container>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <Container>
      <Row>
        <Header
          title="Detalle de reporte"
          items={[
            { label: "Listado de dispositivos", to: "/sensors" },
            { label: "Detalle de dispositivo", to: `/sensors/${report.sensor_id}` },
            {
              label: report.report_category === 'maintance' ? "Listado de mantenimientos" : "Listado de fallas",
              to: `/sensors/${report.sensor_id}/${report.report_category === 'maintance' ? 'maintance' : 'failure'}`
            },
            { label: "Detalle de reporte" }
          ]}
        />
      </Row>

      <Row className="mb-2">
        <Col className="col-12 col-lg-6">
          <Card className="shadow-sm mb-3">
            <Card.Header>Información del reporte</Card.Header>
            <Card.Body>
              <div className="form-group">
                <label className="form-label font-weight-bold">Fecha</label>
                <p className="m-0">{formatAlertDate(report.created_at)}</p>
              </div>

              <div className="form-group">
                <label className="form-label font-weight-bold">Usuario</label>
                <p className="m-0">{report.user_name}</p>
              </div>

              <div className="form-group">
                <label className="form-label font-weight-bold">Tipo</label>
                <p className="m-0">{report.report_type}</p>
              </div>

              <div className="form-group">
                <label className="form-label font-weight-bold">Dispositivo</label>
                <p className="m-0">
                  <Link to={`/sensors/${report.sensor_id}`}>
                    {report.sensor_name}
                  </Link>
                </p>
              </div>

              <div className="form-group">
                <label className="form-label font-weight-bold">Comentario</label>
                <p className="m-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {report.comments}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col className="col-12 col-lg-6">
          <Card className="shadow-sm mb-3">
            <Card.Header>Evidencias</Card.Header>
            <Card.Body>
              <EvidencesList evidences={report.evidences} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
